import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import reportWebVitals from './reportWebVitals';
import {
  initClientTelemetry,
  reportGlobalErrorEvent,
  reportUnhandledRejectionEvent,
  markPersistedErrorTransported,
} from './services/clientTelemetry';

// Client error telemetry (docs/CLIENT_ERROR_TELEMETRY_PLAN.md). Started here, at module
// scope and BEFORE the safe-mode branch below, so that queued events are still delivered
// when the app itself cannot boot. It has no dependency on the DB, Amplitude or React.
initClientTelemetry();

// Function to dynamically load external scripts
const loadScript = (src, async = true) => {
    return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
            resolve(); // Script already loaded
            return;
        }
        const script = document.createElement('script');
        script.src = src;
        script.async = async;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
    });
};

// Load Amplitude
const loadAmplitude = async () => {
    await loadScript("https://cdn.amplitude.com/script/392c896b26bd3fca28a4f4a09356ee31.js");

    if (window.amplitude && window.sessionReplay) {
        // Lowered from 1.0 — 100% replay was capturing base64 data-URI image src attributes
        // from the gallery into the in-memory buffer, causing tab OOM crashes for users
        // with non-trivial generation history. 1% retains diagnostic coverage.
        window.amplitude.add(window.sessionReplay.plugin({ sampleRate: 0.01 }));
        window.amplitude.init('392c896b26bd3fca28a4f4a09356ee31', {
            fetchRemoteConfig: true,
            autocapture: true,
        });
    } else {
        console.warn("Amplitude or sessionReplay not available on window");
    }
};

if (!window.__SAFE_MODE__ && !window.__LS_BLOATED__) {
  loadAmplitude();
} else {
  console.warn('[Crash mitigation] Amplitude skipped due to safe mode or LS bloat.');
}

const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const isDev = window.location.hostname.includes('dev.');
if (!isLocalhost && !isDev) {
    window.__originalConsoleLog = console.log;
    console.log = () => {};
}

// Global error handling to prevent white screens
window.addEventListener('error', (event) => {
  console.error('Global error caught:', event.error);
  // Report first (metadata only; the same Event is de-duplicated against App.js's listener),
  // so that even React's own errors, skipped below, are counted.
  reportGlobalErrorEvent(event);
  
  // Don't interfere with React's own error handling
  if (event.error && event.error.message && 
      (event.error.message.includes('React') || 
       event.error.message.includes('Minified React error'))) {
    return;
  }
  
  // Store error info for debugging
  try {
    const rootErrorTs = Date.now();
    localStorage.setItem('root_error', JSON.stringify({
      timestamp: rootErrorTs,
      message: event.error?.message || event.message || 'Unknown error',
      stack: event.error?.stack || 'No stack trace'
    }));
    markPersistedErrorTransported('root_error', rootErrorTs);
    
    // Set recovery data to help app restore state
    localStorage.setItem('crash_recovery', 'true');
    
    // Create a brief message to the user
    const errorDiv = document.createElement('div');
    errorDiv.style.position = 'fixed';
    errorDiv.style.top = '50%';
    errorDiv.style.left = '50%';
    errorDiv.style.transform = 'translate(-50%, -50%)';
    errorDiv.style.backgroundColor = 'rgba(30, 17, 78, 0.95)';
    errorDiv.style.color = 'white';
    errorDiv.style.padding = '20px';
    errorDiv.style.borderRadius = '8px';
    errorDiv.style.zIndex = '9999';
    errorDiv.style.maxWidth = '80%';
    errorDiv.style.textAlign = 'center';
    errorDiv.innerHTML = `
      <h3 style="margin-top: 0; color: #FF5555;">Error Detected</h3>
      <p>The application encountered an error.</p>
      <button style="background: #6B46C1; border: none; color: white; padding: 8px 16px; border-radius: 4px; cursor: pointer; margin-top: 10px;" 
              onclick="window.location.reload()">
        Reload Application
      </button>
    `;
    
    // Only append if not already there
    if (!document.getElementById('global-error-message')) {
      errorDiv.id = 'global-error-message';
      document.body.appendChild(errorDiv);
    }
  } catch (e) {
    console.error('Error in error handler:', e);
  }
});

// Unhandled promise rejections are also captured by App.js, but App.js never loads in safe
// mode or when the App import fails — exactly when this signal matters most. The telemetry
// layer de-duplicates the shared Event object, so each rejection is reported once.
window.addEventListener('unhandledrejection', (event) => {
  reportUnhandledRejectionEvent(event);
});

/* ── Recovery shell ──────────────────────────────────────────────────────
     When the crash-recovery circuit breaker has tripped (__SAFE_MODE__ or
     __LS_BLOATED__), do NOT mount React at all — render a dependency-free
     vanilla-HTML recovery UI so nothing in the app tree can re-trigger
     the crash.
──────────────────────────────────────────────────────────────────────── */
function renderRecoveryShell() {
  var rootEl = document.getElementById('root');
  if (!rootEl) return;

  var diag = {};
  try { diag = JSON.parse(localStorage.getItem('__crash_diag__') || '{}'); } catch(e) {}
  var storageEst = null;
  try { storageEst = JSON.parse(localStorage.getItem('__crash_storage_estimate__') || 'null'); } catch(e) {}
  var crashCount = localStorage.getItem('__boot_crash_count__') || '?';
  var lastAction = localStorage.getItem('__crash_recovery_action__') || 'none';
  var suggestedStep = null;
  try { suggestedStep = localStorage.getItem('__crash_recovery_suggested_step__'); } catch(e) {}
  if (suggestedStep !== '1' && suggestedStep !== '2' && suggestedStep !== '3' && suggestedStep !== '4') {
    suggestedStep = null;
  }
  var recBadge = function(step) {
    return suggestedStep === step
      ? '<span style="display:inline-block;margin-left:10px;padding:2px 9px;background:white;color:#190A4E;border-radius:10px;font-size:11px;font-weight:bold;letter-spacing:0.5px;vertical-align:middle">Recommended</span>'
      : '';
  };
  var totalMB = diag.totalMB || '?';
  var largeKeys = diag.largeKeys || {};
  var prevCrumb = '';
  try { prevCrumb = localStorage.getItem('__crash_crumb_prev__') || '(none)'; } catch(e) { prevCrumb = '(unreadable)'; }
  var currCrumb = '';
  try { currCrumb = localStorage.getItem('__crash_crumb__') || '(none)'; } catch(e) { currCrumb = '(unreadable)'; }
  var largeKeysHtml = '';
  for (var k in largeKeys) {
    if (Object.prototype.hasOwnProperty.call(largeKeys, k)) {
      largeKeysHtml += '<li><code>' + k + '</code>: ' + largeKeys[k] + ' MB</li>';
    }
  }
  if (!largeKeysHtml) largeKeysHtml = '<li><em>(no localStorage keys over 100 KB)</em></li>';

  // Suggested-next-step banner — rendered only when the boot shim wrote a
  // suggested step (i.e. the shell is here because of a real crash count,
  // not just `?safe=1`). The other steps stay available below.
  var suggestionBannerHtml = '';
  if (suggestedStep) {
    var stepFriendly = {
      '1': 'Step 1 \u2014 Try loading Promptus again',
      '2': 'Step 2 \u2014 Clear cached media',
      '3': 'Step 3 \u2014 Full local reset',
      '4': 'Step 4 \u2014 Browser-level recovery'
    }[suggestedStep];
    suggestionBannerHtml =
      '<div style="background:rgba(255,176,46,0.12);border:1px solid rgba(255,176,46,0.5);padding:14px 16px;border-radius:8px;margin:0 0 16px">' +
      '  <p style="margin:0;font-size:14px;line-height:1.5"><strong style="color:#FFB02E">Suggested next step:</strong> Based on the previous attempts, we recommend trying <strong>' + stepFriendly + '</strong>. Every step is still available below in case it doesn\u2019t resolve things.</p>' +
      '</div>';
  }

  // Storage estimate banner — only render if we have data to show, and only
  // raise the warning treatment when usage is high enough to matter.
  var storageBannerHtml = '';
  var storageHighUsage = false;
  if (storageEst && (storageEst.usageMB !== null && storageEst.usageMB !== undefined)) {
    var pctText = (storageEst.pct !== null && storageEst.pct !== undefined)
      ? storageEst.pct + '%'
      : '—';
    storageHighUsage = (storageEst.pct !== null && storageEst.pct !== undefined && storageEst.pct >= 70);
    var bannerBg = storageHighUsage ? 'rgba(255,176,46,0.10)' : 'rgba(255,255,255,0.04)';
    var bannerBorder = storageHighUsage ? '1px solid rgba(255,176,46,0.45)' : '1px solid rgba(255,255,255,0.08)';
    var bannerNote = storageHighUsage
      ? '<p style="margin:6px 0 0;color:#FFB02E;font-size:13px">Your local cache is using a large share of the browser\u2019s storage quota. If the quick fixes below don\u2019t get you back in, the <strong>Browser-level recovery</strong> section further down is the fastest path.</p>'
      : '<p style="margin:6px 0 0;font-size:12px;opacity:0.75">Your storage usage is comfortably within the browser\u2019s quota.</p>';
    storageBannerHtml =
      '<div style="background:' + bannerBg + ';border:' + bannerBorder + ';padding:14px 16px;border-radius:8px;margin:0 0 16px">' +
      '  <p style="margin:0;font-size:14px"><strong>Cached app data:</strong> ' +
      (storageEst.usageMB !== null ? storageEst.usageMB + ' MB' : '—') +
      (storageEst.quotaMB !== null ? ' of ' + storageEst.quotaMB + ' MB available' : '') +
      ' &nbsp;<span style="opacity:0.7">(' + pctText + ')</span></p>' +
      bannerNote +
      '</div>';
  }

  rootEl.innerHTML =
    '<div style="background:#190A4E;color:white;font-family:Titillium Web,sans-serif;' +
    'min-height:100vh;display:flex;align-items:flex-start;justify-content:center;padding:32px 24px;box-sizing:border-box">' +
    '  <div style="max-width:680px;width:100%">' +
    '    <h1 style="color:#FFB02E;margin:0 0 6px;font-size:28px">Welcome back to Promptus</h1>' +
    '    <p style="margin:0 0 8px;font-size:16px;line-height:1.5">Something went wrong while the app was loading, so we paused everything to keep you safe. Don\u2019t worry &mdash; this is a recovery screen, not an error you caused, and we\u2019ll get you back in.</p>' +
    '    <p style="margin:0 0 20px;font-size:14px;line-height:1.5;opacity:0.85">Your account, generations, credits, and saved work are stored on our servers and are <strong>not affected</strong> by anything on this page. The options below only touch the local cache your browser keeps for offline use.</p>' +
    storageBannerHtml +
    suggestionBannerHtml +
    '    <div style="background:rgba(18,206,198,0.10);border:1px solid rgba(18,206,198,0.4);padding:18px;border-radius:10px;margin:0 0 16px">' +
    '      <p style="margin:0 0 4px;font-size:13px;text-transform:uppercase;letter-spacing:0.5px;color:#12cec6;font-weight:bold">Step 1 \u2014 Try the easy fix first' + recBadge('1') + '</p>' +
    '      <p style="margin:0 0 12px;font-size:14px;line-height:1.5">A lot of the time this is a one-off and a fresh start is all you need. Click the button below and the app will try to load normally again.</p>' +
    '      <button id="rec-retry" style="display:block;width:100%;padding:14px;background:#12cec6;color:#190A4E;border:none;border-radius:6px;font-weight:bold;cursor:pointer;font-size:16px">Try loading Promptus again</button>' +
    '    </div>' +
    '    <div style="background:rgba(255,255,255,0.05);padding:18px;border-radius:10px;margin:0 0 16px">' +
    '      <p style="margin:0 0 4px;font-size:13px;text-transform:uppercase;letter-spacing:0.5px;color:#FFB02E;font-weight:bold">Step 2 \u2014 Clear cached media' + recBadge('2') + '</p>' +
    '      <p style="margin:0 0 12px;font-size:14px;line-height:1.5">If Step 1 brought you straight back here, the issue might be with media saved in your browser. These options each clear a little more &mdash; try them top to bottom.</p>' +
    '      <button id="rec-clear-media" style="display:block;width:100%;padding:12px;margin:0 0 8px;background:#FFB02E;color:#190A4E;border:none;border-radius:6px;font-weight:bold;cursor:pointer;font-size:14px">Clear suspect cached media &amp; retry</button>' +
    '      <button id="rec-clear-images" style="display:block;width:100%;padding:12px;margin:0;background:#FFB02E;color:#190A4E;border:none;border-radius:6px;font-weight:bold;cursor:pointer;font-size:14px">Clear local gallery cache (keeps settings)</button>' +
    '      <p style="margin:10px 0 0;font-size:12px;opacity:0.7;line-height:1.4">Your gallery and settings live on our servers too &mdash; the next time you sign in, anything cleared here re-downloads automatically.</p>' +
    '    </div>' +
    '    <div style="background:rgba(255,255,255,0.05);padding:18px;border-radius:10px;margin:0 0 16px">' +
    '      <p style="margin:0 0 4px;font-size:13px;text-transform:uppercase;letter-spacing:0.5px;color:#f97316;font-weight:bold">Step 3 \u2014 Full local reset' + recBadge('3') + '</p>' +
    '      <p style="margin:0 0 12px;font-size:14px;line-height:1.5">Still not loading? This wipes everything Promptus stores in this browser and takes you back to a fresh sign-in. You won\u2019t lose anything that\u2019s saved to your account.</p>' +
    '      <button id="rec-full-reset" style="display:block;width:100%;padding:12px;background:#f97316;color:white;border:none;border-radius:6px;font-weight:bold;cursor:pointer;font-size:14px">Clear ALL local Promptus data &amp; retry</button>' +
    '    </div>' +
    '    <div style="background:rgba(217,51,51,0.08);border:1px solid rgba(217,51,51,0.45);padding:18px;border-radius:10px;margin:0 0 16px">' +
    '      <p style="margin:0 0 4px;font-size:13px;text-transform:uppercase;letter-spacing:0.5px;color:#ff7a7a;font-weight:bold">Step 4 \u2014 Last resort: browser-level recovery' + recBadge('4') + '</p>' +
    '      <p style="margin:0 0 12px;font-size:14px;line-height:1.5">In rare cases, the browser\u2019s own storage gets into a state where the buttons above can\u2019t fix it (you might notice them not responding, or this page itself reloading by itself). When that happens, the only reliable fix is to ask the browser directly to clear Promptus\u2019s data. <strong>This is safe</strong> &mdash; your account, generations, and credits are kept on our servers.</p>' +
    '      <details style="margin:0 0 8px"><summary style="cursor:pointer;color:#FFB02E;font-weight:bold;padding:6px 0">Show me how on Chrome / Edge / Brave</summary>' +
    '        <ol style="margin:8px 0 0 22px;padding:0;font-size:14px;line-height:1.6">' +
    '          <li>Open a new tab and paste this address: <code style="background:rgba(255,255,255,0.1);padding:2px 6px;border-radius:4px">chrome://settings/content/all</code></li>' +
    '          <li>In the search box, type <strong>promptus</strong>.</li>' +
    '          <li>Click the entry for the Promptus site, then click <strong>Delete data</strong>.</li>' +
    '          <li>Come back to this tab and reload. You\u2019ll be asked to sign in again.</li>' +
    '        </ol>' +
    '      </details>' +
    '      <details style="margin:0 0 8px"><summary style="cursor:pointer;color:#FFB02E;font-weight:bold;padding:6px 0">Show me how on Opera / OperaGX</summary>' +
    '        <ol style="margin:8px 0 0 22px;padding:0;font-size:14px;line-height:1.6">' +
    '          <li>Open Settings &rarr; Privacy &amp; security &rarr; <strong>Site Settings</strong>.</li>' +
    '          <li>Scroll down and click <strong>View permissions and data stored across sites</strong>.</li>' +
    '          <li>Search for <strong>promptus</strong>, click the entry, and choose <strong>Delete</strong>.</li>' +
    '          <li>Reload this tab. You\u2019ll be asked to sign in again.</li>' +
    '        </ol>' +
    '      </details>' +
    '      <details style="margin:0 0 8px"><summary style="cursor:pointer;color:#FFB02E;font-weight:bold;padding:6px 0">Show me how on Firefox</summary>' +
    '        <ol style="margin:8px 0 0 22px;padding:0;font-size:14px;line-height:1.6">' +
    '          <li>Open Settings &rarr; Privacy &amp; Security &rarr; <strong>Cookies and Site Data</strong>.</li>' +
    '          <li>Click <strong>Manage Data\u2026</strong>, search for <strong>promptus</strong>, and click <strong>Remove Selected</strong>.</li>' +
    '          <li>Click <strong>Save Changes</strong>, then reload this tab.</li>' +
    '        </ol>' +
    '      </details>' +
    '      <details style="margin:0 0 0"><summary style="cursor:pointer;color:#FFB02E;font-weight:bold;padding:6px 0">Show me how on Safari</summary>' +
    '        <ol style="margin:8px 0 0 22px;padding:0;font-size:14px;line-height:1.6">' +
    '          <li>Open Safari &rarr; Settings &rarr; <strong>Privacy</strong>.</li>' +
    '          <li>Click <strong>Manage Website Data\u2026</strong>, search for <strong>promptus</strong>.</li>' +
    '          <li>Select the entry and click <strong>Remove</strong>, then <strong>Done</strong>.</li>' +
    '          <li>Reload this tab.</li>' +
    '        </ol>' +
    '      </details>' +
    '    </div>' +
    '    <details style="margin:24px 0 0">' +
    '      <summary style="cursor:pointer;color:rgba(255,255,255,0.6);font-size:13px;padding:8px 0">Show technical details (for support)</summary>' +
    '      <div style="background:rgba(255,255,255,0.04);padding:14px 16px;border-radius:8px;margin:8px 0 16px;font-size:13px">' +
    '        <p style="margin:4px 0"><strong>Crash count:</strong> ' + crashCount + '</p>' +
    '        <p style="margin:4px 0"><strong>Last action:</strong> ' + lastAction + '</p>' +
    '        <p style="margin:4px 0"><strong>Suggested step:</strong> ' + (suggestedStep || '(none)') + '</p>' +
    '        <p style="margin:4px 0"><strong>localStorage total:</strong> ' + totalMB + ' MB</p>' +
    (storageEst ?
      '        <p style="margin:4px 0"><strong>Browser storage usage:</strong> ' +
      (storageEst.usageMB !== null ? storageEst.usageMB + ' MB' : '—') +
      (storageEst.quotaMB !== null ? ' / ' + storageEst.quotaMB + ' MB' : '') +
      (storageEst.pct !== null ? ' (' + storageEst.pct + '%)' : '') +
      '</p>'
      : '') +
    '        <p style="margin:4px 0"><strong>Last crumb (this boot):</strong> <code>' + currCrumb + '</code></p>' +
    '        <p style="margin:4px 0"><strong>Last crumb (previous boot):</strong> <code>' + prevCrumb + '</code></p>' +
    '        <p style="margin:8px 0 4px"><strong>Large keys:</strong></p>' +
    '        <ul style="margin:0 0 0 20px;padding:0">' + largeKeysHtml + '</ul>' +
    '      </div>' +
    '      <div id="rec-idb-probe" style="background:rgba(255,255,255,0.04);padding:14px 16px;border-radius:8px;margin:0 0 12px">' +
    '        <p style="margin:0 0 8px;font-size:13px"><strong>IndexedDB probe</strong> &mdash; counts records per store. Heads-up: opening the database from this page has crashed the tab on previously affected machines, so this is opt-in.</p>' +
    '        <button id="rec-probe-btn" style="display:block;width:100%;padding:8px;background:transparent;color:white;border:1px solid white;border-radius:6px;cursor:pointer;font-size:13px">Probe IndexedDB record counts</button>' +
    '      </div>' +
    '      <div id="rec-idb-deldiag" style="background:rgba(18,206,198,0.06);border:1px solid rgba(18,206,198,0.3);padding:14px 16px;border-radius:8px;margin:0">' +
    '        <p style="margin:0 0 8px;font-size:13px"><strong style="color:#12cec6">Open-less deleteDatabase diagnostic</strong> &mdash; calls <code>indexedDB.deleteDatabase()</code> directly without opening the DB. Reports its outcome here without redirecting; useful for confirming whether the open path is the bottleneck.</p>' +
    '        <button id="rec-deldiag-btn" style="display:block;width:100%;padding:8px;background:transparent;color:#12cec6;border:1px solid #12cec6;border-radius:6px;cursor:pointer;font-size:13px">Run open-less deleteDatabase diagnostic</button>' +
    '        <div id="rec-deldiag-status" style="margin-top:10px;font-size:12px;font-family:Consolas,monospace;white-space:pre-wrap"></div>' +
    '      </div>' +
    '    </details>' +
    '  </div>' +
    '</div>';

  var clearMediaKeys = function () {
    var keys = ['profilePhoto','temp_image_data','selected_image','emergency_fallback_image','image_data_url','image_base64_raw','momm_imported_image','image_transfer_active','page_transition','navigation_timestamp'];
    keys.forEach(function (k) { try { localStorage.removeItem(k); } catch(e) {} });
    try { localStorage.setItem('__boot_crash_count__', '0'); } catch(e) {}
    try { localStorage.removeItem('__boot_started_at__'); } catch(e) {}
    try { localStorage.removeItem('__boot_completed_at__'); } catch(e) {}
    try { localStorage.removeItem('__crash_recovery_suggested_step__'); } catch(e) {}
    window.location.replace(window.location.pathname);
  };

  var fullReset = function () {
    try {
      if (navigator.serviceWorker) {
        navigator.serviceWorker.getRegistrations().then(function (regs) {
          regs.forEach(function (r) { r.unregister(); });
        });
      }
    } catch(e) {}
    try { localStorage.clear(); } catch(e) {}
    try { sessionStorage.clear(); } catch(e) {}
    try { indexedDB.deleteDatabase('promptus-pwa-db'); } catch(e) {}
    try {
      if (window.caches) {
        caches.keys().then(function (names) {
          names.forEach(function (n) { caches.delete(n); });
        });
      }
    } catch(e) {}
    setTimeout(function () { window.location.replace(window.location.pathname); }, 500);
  };

  var tryNormal = function () {
    try { localStorage.setItem('__boot_crash_count__', '0'); } catch(e) {}
    try { localStorage.removeItem('__boot_started_at__'); } catch(e) {}
    try { localStorage.removeItem('__boot_completed_at__'); } catch(e) {}
    try { localStorage.removeItem('__crash_recovery_suggested_step__'); } catch(e) {}
    window.location.replace(window.location.pathname);
  };

  var probeIndexedDB = function () {
    var probeEl = document.getElementById('rec-idb-probe');
    if (!probeEl) return;
    if (!window.indexedDB) {
      probeEl.innerHTML = '<p style="margin:0;color:#FFB02E">IndexedDB not available</p>';
      return;
    }
    try {
      var req = indexedDB.open('promptus-pwa-db');
      req.onerror = function () {
        probeEl.innerHTML = '<p style="margin:0;color:#FFB02E">IDB open error: ' + (req.error && req.error.message || 'unknown') + '</p>';
      };
      req.onblocked = function () {
        probeEl.innerHTML = '<p style="margin:0;color:#FFB02E">IDB open blocked (close other tabs)</p>';
      };
      req.onsuccess = function () {
        var dbHandle = req.result;
        var storeNames = [];
        try {
          for (var sIdx = 0; sIdx < dbHandle.objectStoreNames.length; sIdx++) {
            storeNames.push(dbHandle.objectStoreNames[sIdx]);
          }
        } catch (e) {
          probeEl.innerHTML = '<p style="margin:0;color:#FFB02E">IDB enum error: ' + (e && e.message) + '</p>';
          try { dbHandle.close(); } catch (_e) {}
          return;
        }
        if (storeNames.length === 0) {
          probeEl.innerHTML = '<p style="margin:0"><em>(no object stores)</em></p>';
          try { dbHandle.close(); } catch (_e) {}
          return;
        }
        var results = {};
        var pending = storeNames.length;
        var finalize = function () {
          var rowsHtml = '';
          var sorted = Object.keys(results).sort(function (a, b) {
            var av = typeof results[a] === 'number' ? results[a] : -1;
            var bv = typeof results[b] === 'number' ? results[b] : -1;
            return bv - av;
          });
          for (var i = 0; i < sorted.length; i++) {
            var n = sorted[i];
            var c = results[n];
            var color = (typeof c === 'number' && c > 1000) ? '#FFB02E' : 'white';
            rowsHtml += '<li><code>' + n + '</code>: <span style="color:' + color + '">' + c + '</span></li>';
          }
          probeEl.innerHTML =
            '<p style="margin:0 0 6px"><strong>IndexedDB record counts:</strong></p>' +
            '<ul style="margin:0 0 0 20px;padding:0">' + rowsHtml + '</ul>' +
            '<p style="margin:8px 0 0;font-size:12px;opacity:0.7">Counts only \u2014 no record data was read. High counts on image stores indicate likely OOM source.</p>';
          try { dbHandle.close(); } catch (_e) {}
        };
        try {
          var tx = dbHandle.transaction(storeNames, 'readonly');
          for (var si = 0; si < storeNames.length; si++) {
            (function (storeName) {
              try {
                var store = tx.objectStore(storeName);
                var cReq = store.count();
                cReq.onsuccess = function () {
                  results[storeName] = cReq.result;
                  pending--;
                  if (pending === 0) finalize();
                };
                cReq.onerror = function () {
                  results[storeName] = 'err';
                  pending--;
                  if (pending === 0) finalize();
                };
              } catch (e) {
                results[storeName] = 'err';
                pending--;
                if (pending === 0) finalize();
              }
            })(storeNames[si]);
          }
        } catch (e) {
          probeEl.innerHTML = '<p style="margin:0;color:#FFB02E">IDB tx error: ' + (e && e.message) + '</p>';
          try { dbHandle.close(); } catch (_e) {}
        }
      };
    } catch (e) {
      probeEl.innerHTML = '<p style="margin:0;color:#FFB02E">IDB probe error: ' + (e && e.message) + '</p>';
    }
  };

  var clearImageStores = function () {
    var btn = document.getElementById('rec-clear-images');
    if (btn) { btn.disabled = true; btn.textContent = 'Deleting image stores\u2026'; }
    if (!window.indexedDB) { window.location.replace(window.location.pathname); return; }
    try {
      var req = indexedDB.open('promptus-pwa-db');
      req.onerror = function () {
        try { localStorage.setItem('__boot_crash_count__', '0'); } catch(e) {}
        try { localStorage.removeItem('__boot_started_at__'); } catch(e) {}
        try { localStorage.removeItem('__boot_completed_at__'); } catch(e) {}
        try { localStorage.removeItem('__crash_recovery_suggested_step__'); } catch(e) {}
        window.location.replace(window.location.pathname);
      };
      req.onblocked = function () {
        if (btn) {
          btn.disabled = false;
          btn.textContent = 'Blocked: close other Promptus tabs first, then retry';
          btn.style.background = '#d33';
          btn.style.color = 'white';
        }
      };
      req.onsuccess = function () {
        var dbHandle = req.result;
        var targets = ['images', 'outputImages', 'collectedImages'];
        var available = [];
        for (var t = 0; t < targets.length; t++) {
          if (dbHandle.objectStoreNames.contains(targets[t])) available.push(targets[t]);
        }
        if (available.length === 0) {
          try { dbHandle.close(); } catch (_e) {}
          try { localStorage.setItem('__boot_crash_count__', '0'); } catch(e) {}
          try { localStorage.removeItem('__boot_started_at__'); } catch(e) {}
          try { localStorage.removeItem('__boot_completed_at__'); } catch(e) {}
          try { localStorage.removeItem('__crash_recovery_suggested_step__'); } catch(e) {}
          window.location.replace(window.location.pathname);
          return;
        }
        try {
          var tx = dbHandle.transaction(available, 'readwrite');
          for (var i = 0; i < available.length; i++) {
            try { tx.objectStore(available[i]).clear(); } catch (e) {}
          }
          tx.oncomplete = function () {
            try { dbHandle.close(); } catch (_e) {}
            try { localStorage.setItem('__boot_crash_count__', '0'); } catch(e) {}
            try { localStorage.removeItem('__boot_started_at__'); } catch(e) {}
            try { localStorage.removeItem('__boot_completed_at__'); } catch(e) {}
            try { localStorage.removeItem('__crash_recovery_suggested_step__'); } catch(e) {}
            window.location.replace(window.location.pathname);
          };
          tx.onerror = function () {
            try { dbHandle.close(); } catch (_e) {}
            try { localStorage.setItem('__boot_crash_count__', '0'); } catch(e) {}
            try { localStorage.removeItem('__boot_started_at__'); } catch(e) {}
            try { localStorage.removeItem('__boot_completed_at__'); } catch(e) {}
            try { localStorage.removeItem('__crash_recovery_suggested_step__'); } catch(e) {}
            window.location.replace(window.location.pathname);
          };
        } catch (e) {
          try { dbHandle.close(); } catch (_e) {}
          window.location.replace(window.location.pathname);
        }
      };
    } catch (e) {
      window.location.replace(window.location.pathname);
    }
  };

  var deleteDatabaseOpenless = function () {
    var btn = document.getElementById('rec-deldiag-btn');
    var statusEl = document.getElementById('rec-deldiag-status');
    if (!statusEl) return;
    if (!window.indexedDB) {
      statusEl.textContent = 'IndexedDB not available on this browser.';
      return;
    }
    if (btn) { btn.disabled = true; btn.textContent = 'Running\u2026'; }

    var t0 = Date.now();
    var append = function (line) {
      var elapsed = Date.now() - t0;
      statusEl.textContent += '[+' + elapsed + 'ms] ' + line + '\n';
    };
    statusEl.textContent = '';
    append('Calling indexedDB.deleteDatabase("promptus-pwa-db")\u2026');

    // Heartbeat: if no event fires within 15s the request is effectively
    // hung (likely blocked by an open connection from a leaked tab/worker).
    var heartbeat = setInterval(function () {
      append('still pending\u2026');
    }, 5000);
    var settled = false;
    var settle = function () {
      if (settled) return;
      settled = true;
      clearInterval(heartbeat);
      if (btn) { btn.disabled = false; btn.textContent = 'Run open-less deleteDatabase diagnostic'; }
    };

    try {
      var req = window.indexedDB.deleteDatabase('promptus-pwa-db');
      req.onsuccess = function () {
        append('SUCCESS \u2014 database deleted. The open path was the bottleneck. You can now click "Try normal boot" to see if the app recovers.');
        settle();
      };
      req.onerror = function () {
        var msg = (req.error && (req.error.name + ': ' + req.error.message)) || 'unknown error';
        append('ERROR \u2014 ' + msg);
        settle();
      };
      req.onblocked = function () {
        append('BLOCKED \u2014 another tab/worker still holds an open connection. Close all other Promptus tabs (and any service worker) and run this again.');
        // Do NOT settle: a subsequent close from the other tab can still
        // cause onsuccess to fire. Keep the heartbeat going so user sees it.
      };
      req.onupgradeneeded = function () {
        // Should not happen on a delete request, but log if it does.
        append('upgradeneeded fired (unexpected on delete) \u2014 letting it complete\u2026');
      };
    } catch (e) {
      append('THREW \u2014 ' + (e && (e.name + ': ' + e.message) || String(e)));
      settle();
    }
  };

  document.getElementById('rec-clear-media').addEventListener('click', clearMediaKeys);
  document.getElementById('rec-clear-images').addEventListener('click', clearImageStores);
  document.getElementById('rec-full-reset').addEventListener('click', fullReset);
  document.getElementById('rec-retry').addEventListener('click', tryNormal);

  var probeBtn = document.getElementById('rec-probe-btn');
  if (probeBtn) {
    probeBtn.addEventListener('click', function () {
      probeBtn.disabled = true;
      probeBtn.textContent = 'Probing\u2026';
      probeIndexedDB();
    });
  }

  var deldiagBtn = document.getElementById('rec-deldiag-btn');
  if (deldiagBtn) {
    deldiagBtn.addEventListener('click', deleteDatabaseOpenless);
  }
}

/* ── Crash-recovery boot-success markers ────────────────────────────────
     These ONLY arm in the success path of the App import — i.e., after
     React has actually mounted. Arming them at module scope (regardless of
     safe-mode / import success) caused user clicks on the recovery shell
     to falsely mark the boot as stable, resetting __boot_crash_count__
     to 0 and preventing the circuit breaker from ever escalating.

     Only mark boot stable when BOTH conditions are true:
       (a) React has rendered (the .then() callback ran)
       (b) the tab has survived 12+ seconds since render
       (c) the user has actually interacted with the page (click or keydown)
──────────────────────────────────────────────────────────────────────── */
var bootSurvivalMet = false;
var bootInteractionMet = false;
var bootMarked = false;

var maybeMarkBootSuccess = function () {
  if (bootMarked) return;
  if (!bootSurvivalMet || !bootInteractionMet) return;
  bootMarked = true;
  try {
    localStorage.setItem('__boot_completed_at__', String(Date.now()));
    localStorage.setItem('__boot_crash_count__', '0');
    console.warn('[Crash recovery] Boot marked stable (React mounted + 12s survived + user interacted).');
  } catch (e) {}
  window.removeEventListener('click', onUserInteract, true);
  window.removeEventListener('keydown', onUserInteract, true);
};

var onUserInteract = function () {
  bootInteractionMet = true;
  maybeMarkBootSuccess();
};

var armBootSuccessMarkers = function () {
  setTimeout(function () {
    bootSurvivalMet = true;
    maybeMarkBootSuccess();
  }, 12000);
  window.addEventListener('click', onUserInteract, true);
  window.addEventListener('keydown', onUserInteract, true);
};

if (window.__SAFE_MODE__ || window.__LS_BLOATED__) {
  console.warn('[Crash mitigation] Safe mode active — App import skipped, rendering recovery shell.');
  // Permanently disable boot-success marking — we do not want any user
  // interaction with the recovery shell to be interpreted as a successful
  // boot.
  bootMarked = true;
  renderRecoveryShell();
} else {
  // Dynamic import: only loads App and its dependency tree when NOT in safe mode.
  // Static imports execute at module-init, before any runtime gate can intervene,
  // so any module-level side effect in the App tree could crash the tab before
  // safe mode gets to render its fallback. Dynamic import defers all of that.
  try { localStorage.setItem('__crash_crumb__', 'index:before-app-import@' + Date.now()); } catch (e) {}
  import('./App').then(function (mod) {
    try { localStorage.setItem('__crash_crumb__', 'index:app-imported@' + Date.now()); } catch (e) {}
    var App = mod.default;
    var root = ReactDOM.createRoot(document.getElementById('root'));
    root.render(
      <React.StrictMode>
        <App />
      </React.StrictMode>
    );
    try { localStorage.setItem('__crash_crumb__', 'index:react-rendered@' + Date.now()); } catch (e) {}
    // Only NOW arm the boot-success markers. Survival timer is measured
    // from React render time, not module-load time, so it represents 12s
    // of actual app being mounted.
    armBootSuccessMarkers();
  }).catch(function (err) {
    console.error('[Crash mitigation] App dynamic import failed:', err);
    try { localStorage.setItem('__crash_crumb__', 'index:app-import-failed@' + Date.now()); } catch (e) {}
    try { localStorage.setItem('__app_import_error__', String(err && err.stack || err)); } catch (e) {}
    // Permanently disable boot-success marking — a failed import must not
    // be marked as stable just because the user clicks the recovery shell.
    bootMarked = true;
    renderRecoveryShell();
  });
}

// Start measuring performance
reportWebVitals();

// Service Worker Registration
// if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
//   window.addEventListener('load', () => {
//     const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;
    
//     navigator.serviceWorker
//       .register(swUrl)
//       .then(registration => {
//         console.log('ServiceWorker registration successful:', registration);
        
//         // Check for updates when the app gains focus
//         document.addEventListener('visibilitychange', () => {
//           if (!document.hidden) {
//             registration.update();
//           }
//         });
        
//         // Listen for update found
//         registration.addEventListener('updatefound', () => {
//           const installingWorker = registration.installing;
          
//           if (installingWorker) {
//             installingWorker.addEventListener('statechange', () => {
//               if (installingWorker.state === 'installed') {
//                 if (navigator.serviceWorker.controller) {
//                   // New update available
//                   console.log('New content is available; please refresh.');
                  
//                   // Show update notification to user
//                   const shouldUpdate = window.confirm(
//                     'A new version of the application is available. ' +
//                     'Would you like to update now?'
//                   );
                  
//                   if (shouldUpdate) {
//                     // Tell the service worker to skip waiting
//                     installingWorker.postMessage({ type: 'SKIP_WAITING' });
//                     window.location.reload(true);
//                   }
//                 } else {
//                   // Content is cached for offline use
//                   console.log('Content is cached for offline use.');
//                 }
//               }
//             });
//           }
//         });
        
//         // Handle controller change (when service worker updates)
//         let refreshing = false;
//         navigator.serviceWorker.addEventListener('controllerchange', () => {
//           if (!refreshing) {
//             refreshing = true;
//             window.location.reload(true);
//           }
//         });
//       })
//       .catch(error => {
//         console.error('ServiceWorker registration failed:', error);
//       });
//   });
  
//   // Listen for messages from service worker
//   navigator.serviceWorker.addEventListener('message', event => {
//     if (event.data && event.data.type === 'SW_UPDATED') {
//       console.log('Service worker updated to version:', event.data.version);
//     }
//   });
// }
